import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { currentBranding } from '../../../config/branding';
import { branding, documentMimeTypes } from '../../../config/enum';
import { NewProgressProps as NewProgressProperties } from '../../../interface/file-upload-stream.interface';
import { useAppDispatch } from '../../../redux/hooks';
import {
  setUnUploadedFileData,
  updateFileUploadProgress,
} from '../../../redux/slice/FileUploadStreamSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneNumber from '../../../shared-components/custom-phone-number-input/CustomPhoneNumberInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotificaion';
import {
  checkFileSizeValidation,
  emailValidation,
  passwordValidation,
  phoneNumberValidation,
  stringValidation,
} from '../../../shared-functions/Validation';
import auth from '../../../utils/api/auth';

import BannerWithSlider from './BannerWithSlider';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}
interface Credentials {
  name: string;
  password: string;
  confirmPassword: string;
  idProof: File[];
  type: string | number;
  emailOrPhoneNumber: string;
  countryCode: string;
  [key: string]: string | number | File[];
}

/**
 * Registration component
 */
function Register() {
  const [fileInputKey, setFileInputKey] = useState(0);
  // const [uploadPercentage, setUploadPercentage] = useState<number>(0);

  const [credentials, setCredentials] = useState<Credentials>({
    name: '',
    password: '',
    confirmPassword: '',
    emailOrPhoneNumber: '',
    idProof: [],
    type: 'email',
    countryCode: currentBranding === 'energic' ? '966' : '1',
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });
  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    hint: true,
    buttonLoader: false,
  });

  /**
   * Function handle register data change
   * @param name
   * @param changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number | File[]) => {
    if (name === 'type') {
      setCredentials({
        ...credentials,
        [name]: changeValue as string | number,
        emailOrPhoneNumber: '',
      });
    } else {
      setCredentials({
        ...credentials,
        [name]: changeValue,
      });
    }
    // If the idProof is being cleared (file removed), reset the key
    if (name === 'idProof' && Array.isArray(changeValue) && changeValue.length === 0) {
      setFileInputKey((previous) => previous + 1);
    }
  };

  /**
   * Function that navigate to login page
   */
  const handleNavigate = () => {
    setModalBoxStatus('close');
    navigate('../login/credentials');
  };

  /**
   * Function to get and handle file upload progress
   */
  const handleUploadProgress = (progress: NewProgressProperties) => {
    if (progress?.fileName && progress?.percentage) {
      dispatch(updateFileUploadProgress(progress));
    }
  };
  /**
   * Register API call
   */
  const handleSubmit = () => {
    setFormStatus({
      showFormErrors: true,
      hint: false,
      buttonLoader: false,
    });
    if (
      credentials.type !== '' &&
      credentials.countryCode !== '' &&
      stringValidation(1, 100, credentials.name) &&
      (phoneNumberValidation(credentials.emailOrPhoneNumber) ||
        emailValidation(credentials.emailOrPhoneNumber)) &&
      passwordValidation(credentials.password) &&
      (process.env.REACT_APP_BRANDING === branding.VOLTIE ||
        checkFileSizeValidation(credentials.idProof, 5, 1, 0, documentMimeTypes)) &&
      credentials.password === credentials.confirmPassword
    ) {
      setFormStatus({
        showFormErrors: true,
        hint: false,
        buttonLoader: true,
      });

      const formData = new FormData();

      formData.append('name', credentials.name);
      formData.append('password', credentials.password);
      if (credentials.type === 'email') {
        formData.append('email', credentials.emailOrPhoneNumber.toLowerCase());
      } else if (credentials.type === 'phone') {
        formData.append('countryCode', credentials.countryCode);
        formData.append('phoneNumber', credentials.emailOrPhoneNumber);
      }

      if (credentials.idProof.length > 0) {
        formData.append('governmentId', credentials.idProof[0]);
      }

      dispatch(
        setUnUploadedFileData([
          {
            // fileName: credentials.idProof[0].name,
            fileName: 'QR_Codes_pdf',
            progress: 0,
            status: 'pending',
          },
        ]),
      );

      auth
        .selfRegistration(formData, handleUploadProgress)
        .then(() => {
          setModalBoxStatus('open');
          setFormStatus({
            showFormErrors: false,
            hint: false,
            buttonLoader: false,
          });
          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title="Account register successfully"
                description="Your account has been registered"
                notificationCloseTimer={3000}
                onNotificationClose={handleNavigate}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: false,
            hint: false,
            buttonLoader: false,
          });
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    }
  };

  return (
    <div className="register-container">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="register-banner">
        <BannerWithSlider />
      </div>
      <div className="register-form-wrap">
        <div className="register-form-container">
          <h1>Sign up</h1>
          <div className="register-form">
            <div className="register-name-wrap">
              <CustomInput
                inputLabel="Full Name"
                type="email"
                placeholder="Enter the Name"
                valueRequired
                name="name"
                value={credentials.name}
                onChangeValue={handleCredentialChange}
                error={!stringValidation(1, 100, credentials.name) && formStatus.showFormErrors}
                errorMessage="Name is Required"
                hint
                hintMessage="Please ensure that the name you provide exactly matches the name on your
                government-issued ID."
              />
              {/* <p className="name-warning">
                Please ensure that the name you provide exactly matches the name on your
                government-issued ID.
              </p> */}
            </div>
            <RadioButton
              name="type"
              value={credentials.type}
              list={[
                {
                  label: 'Email',
                  value: 'email',
                },
                {
                  label: 'Phone Number',
                  value: 'phone',
                },
              ]}
              onChangeValue={handleCredentialChange}
            />
            {credentials.type === 'email' ? (
              <CustomInput
                inputLabel="Email"
                type="text"
                placeholder="Enter the email"
                name="emailOrPhoneNumber"
                value={credentials.emailOrPhoneNumber}
                onChangeValue={handleCredentialChange}
                error={
                  !emailValidation(credentials.emailOrPhoneNumber) && formStatus.showFormErrors
                }
                errorMessage="Invalid email id"
              />
            ) : (
              <CustomPhoneNumber
                inputLabel="Phone Number"
                placeholder="Enter the Phone number"
                name="emailOrPhoneNumber"
                value={credentials.emailOrPhoneNumber}
                onChangeValue={handleCredentialChange}
                error={
                  !phoneNumberValidation(credentials.emailOrPhoneNumber) &&
                  formStatus.showFormErrors
                }
                errorMessage="Invalid phone number"
                countryCode={credentials.countryCode}
              />
            )}
            <CustomFileInput
              key={fileInputKey}
              inputLabel="Government Id"
              acceptedFileType="application/pdf"
              // acceptedFileType="image/*, video/*, application/pdf"
              placeholder="Upload a file"
              valueRequired={process.env.REACT_APP_BRANDING !== branding.VOLTIE}
              name="idProof"
              selectedFiles={credentials.idProof}
              onChangeValue={handleCredentialChange}
              error={
                process.env.REACT_APP_BRANDING !== branding.VOLTIE &&
                !(
                  credentials.idProof.length > 0 &&
                  checkFileSizeValidation(credentials.idProof, 5, 1, 0, documentMimeTypes)
                ) &&
                formStatus.showFormErrors
              }
              errorMessage="Government Id proof is required"
            />
            <CustomInput
              inputLabel="Create Password"
              type="password"
              placeholder="Enter password"
              valueRequired
              name="password"
              value={credentials.password}
              onChangeValue={handleCredentialChange}
              error={!passwordValidation(credentials.password) && formStatus.showFormErrors}
              errorMessage="Password must be at least 12 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character( ! @ # $ % ^ & * ( ) _ + [ \ ] { } ; ' : &quot; \\ | , . < > / ? ~ - )."
              hint={
                credentials.password.length === 0 || !passwordValidation(credentials.password)
                  ? formStatus.hint
                  : false
              }
              hintMessage="Password must be 12+ characters, with at least 1 uppercase, 1 lowercase, 1 number, and 1 special character( ! @ # $ % ^ & * ( ) _ + [ \ ] { } ; ' : &quot; \\ | , . < > / ? ~ - )."
            />
            <CustomInput
              inputLabel="Confirm Password"
              type="password"
              placeholder="Re - Enter password"
              valueRequired
              name="confirmPassword"
              value={credentials.confirmPassword}
              onChangeValue={handleCredentialChange}
              error={
                !(credentials.password === credentials.confirmPassword) ||
                (credentials.confirmPassword.length === 0 && formStatus.showFormErrors)
              }
              errorMessage={
                credentials.confirmPassword.length === 0
                  ? 'Confirm password is required.'
                  : 'Password & Confirm password must be same.'
              }
            />
          </div>
          <div className="register-button-wrap">
            <MyButton
              label="Submit"
              disableHoverEffect
              buttonType="submit"
              onClickFunc={handleSubmit}
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
            <div className="login-account">
              <span>Do you have an account already ?</span>
              <span role="presentation" onClick={() => navigate('../')}>
                Login
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
